import dateFormat from 'dateformat';
import { AxiosRequestConfig } from 'axios';

import api from 'services/api';

import { BankSlipRow } from './bankSlipDisplay';

type DeliveryMethod = 'DOWNLOAD' | 'EMAIL';

const getAccessToken = (): string | null =>
  localStorage.getItem('@UltragazEmpresarial:accessToken');

const getCustomerType = (): string | null =>
  localStorage.getItem('@UltragazEmpresarial:customerType');

const getCustomerCodes = (
  invoice: BankSlipRow['invoice']
): { customerCode: string; customerSiteCode: string } => {
  const customerCode =
    invoice.customer?.code ||
    localStorage.getItem('@UltragazEmpresarial:customerCode') ||
    '';
  const customerSiteCode =
    invoice.customer?.siteCode ||
    localStorage.getItem('@UltragazEmpresarial:customerSiteCode') ||
    '';

  return { customerCode, customerSiteCode };
};

const getApiErrorMessage = (error: any): string => {
  const responseMessage = error?.response?.data?.message;

  if (responseMessage) return responseMessage;

  if (error?.message) return error.message;

  return 'Ocorreu um erro interno na aplicação';
};

const shouldIgnoreError = (error: any): boolean => error?.response?.status === 401;

const downloadBankSlipGranel = async (row: BankSlipRow): Promise<void> => {
  const token = getAccessToken();
  const { customerCode, customerSiteCode } = getCustomerCodes(row.invoice);
  const contactIdSf = localStorage.getItem('@UltragazEmpresarial:contactIdSf');

  const body = {
    customerCode,
    customerSiteCode,
    deliveryMethod: 'DOWNLOAD' as DeliveryMethod,
    ...(contactIdSf && { contactIdSf }),
  };

  const response = await api.post(
    `industrial/invoice/v1/invoices/${row.invoice.invoiceId}/installments/${row.installment.installmentId}/bankslips`,
    body,
    {
      method: 'POST',
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        access_token: token,
      },
    } as AxiosRequestConfig
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${row.installment.installmentId}.pdf`);
  document.body.appendChild(link);
  link.click();

  if (link.parentNode) {
    link.parentNode.removeChild(link);
  }
};

const emailBankSlipGranel = async (
  row: BankSlipRow,
  email: string
): Promise<void> => {
  const token = getAccessToken();
  const { customerCode, customerSiteCode } = getCustomerCodes(row.invoice);
  const contactIdSf = localStorage.getItem('@UltragazEmpresarial:contactIdSf');

  const body = {
    customerCode,
    customerSiteCode,
    deliveryMethod: 'EMAIL' as DeliveryMethod,
    email,
    ...(contactIdSf && { contactIdSf }),
  };

  await api.post(
    `industrial/invoice/v1/invoices/${row.invoice.invoiceId}/installments/${row.installment.installmentId}/bankslips`,
    body,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        access_token: token,
      },
    } as AxiosRequestConfig
  );
};

const downloadBankSlipMi = async (row: BankSlipRow): Promise<void> => {
  const token = getAccessToken();
  const billId = row.invoice.id || row.invoice.invoiceId;
  const email = localStorage.getItem('@UltragazEmpresarial:loginEmail');

  const response = await api.post(
    `industrial/bill/v1/bills/${billId}`,
    {
      deliveryMethod: 'DOWNLOAD',
      email,
      isReduced: false,
    },
    {
      headers: { access_token: token },
      responseType: 'blob',
    }
  );

  const referenceMonth = (row.installment.referenceMonth || '').split('/');
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    `Ultragaz_${(
      dateFormat(
        `${parseInt(referenceMonth[1], 10)}/01/${referenceMonth[0]}`,
        'mmmyyyy'
      ) || ''
    ).toUpperCase()}_${billId}.pdf`
  );
  document.body.appendChild(link);
  link.click();

  if (link.parentNode) {
    link.parentNode.removeChild(link);
  }
};

const emailBankSlipMi = async (
  row: BankSlipRow,
  email: string
): Promise<void> => {
  const token = getAccessToken();
  const billId = row.invoice.id || row.invoice.invoiceId;

  if (!email) {
    throw new Error(
      'Não foi possível enviar o e-mail, por favor verifique em dados cadastrais se possui um e-mail cadastrado e validado'
    );
  }

  await api.post(
    `industrial/invoice/v1/bills/${billId}`,
    {
      deliveryMethod: 'EMAIL',
      email,
    },
    {
      headers: { access_token: token },
    }
  );
};

export const downloadBankSlip = async (row: BankSlipRow): Promise<void> => {
  const customerType = getCustomerType();

  if (customerType === 'MI') {
    await downloadBankSlipMi(row);
    return;
  }

  await downloadBankSlipGranel(row);
};

export const emailBankSlip = async (
  row: BankSlipRow,
  email: string
): Promise<void> => {
  const customerType = getCustomerType();

  if (customerType === 'MI') {
    await emailBankSlipMi(row, email);
    return;
  }

  await emailBankSlipGranel(row, email);
};

export const runBankSlipAction = async (
  row: BankSlipRow,
  method: DeliveryMethod,
  options: { email?: string } = {}
): Promise<void> => {
  try {
    if (method === 'DOWNLOAD') {
      await downloadBankSlip(row);
      return;
    }

    const email =
      options.email?.trim() ||
      localStorage.getItem('@UltragazEmpresarial:loginEmail') ||
      '';

    await emailBankSlip(row, email);
  } catch (error) {
    if (shouldIgnoreError(error)) return;

    throw new Error(getApiErrorMessage(error));
  }
};

type BankSlipDuplicateItem = {
  invoiceId: string;
  installmentId: string;
};

export const emailBankSlipsBulk = async (
  items: BankSlipDuplicateItem[],
  email: string
): Promise<void> => {
  const token = getAccessToken();
  const customerCode = localStorage.getItem('@UltragazEmpresarial:customerCode');
  const customerSiteCode = localStorage.getItem(
    '@UltragazEmpresarial:customerSiteCode'
  );

  await api.post(
    '/industrial/invoice/v1/duplicates',
    {
      customerCode,
      customerSiteCode,
      deliveryMethod: 'EMAIL',
      email,
      bankslips: [...items],
    },
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        access_token: token,
      },
    } as AxiosRequestConfig
  );
};

export const getBankSlipEmailSuccessMessage = (): string => {
  const email = localStorage.getItem('@UltragazEmpresarial:loginEmail');

  return email
    ? `Boleto enviado para o e-mail: ${email}`
    : 'Os boletos foram enviados para o seu e-mail.';
};
